import { useEffect, useState } from "react";
import FeatureKeys from "global/constants/FeatureKeys";
import { useFeatureEnabled } from "hooks/use-feature";
import { fetchAvailableIdvMethods } from "Services/CommonServices";
import {
  IdvSecurityOption,
  idvSecurityOptionsFor,
} from "helpers/identityVerificationHelpers";
import { IdvMethod } from "types/identityVerification";

/**
 * One read per page load, shared by every signer row: which methods have a
 * vendor rarely changes, and a form with ten signers shouldn't ask ten times.
 * A failed read offers nothing and is retried on the next mount.
 */
let availableMethods: Promise<IdvMethod[]> | null = null;

const loadAvailableMethods = () => {
  if (!availableMethods) {
    availableMethods = fetchAvailableIdvMethods().catch(() => {
      availableMethods = null;
      return [];
    });
  }
  return availableMethods;
};

/**
 * The identity verification entries for a signer's security dropdown.
 *
 * Empty unless the account has `auth.identity_verification` on, and limited
 * to the methods a vendor runs on this environment. The signer's current
 * option (`currentKey`) is always kept, so a saved choice can be seen and
 * changed even after it stops being offered.
 */
export const useIdvSecurityOptions = (
  currentKey?: string | null,
): IdvSecurityOption[] => {
  const isIdvEnabled = useFeatureEnabled(FeatureKeys.AUTH_IDENTITY_VERIFICATION);
  const [methods, setMethods] = useState<IdvMethod[]>([]);

  useEffect(() => {
    if (!isIdvEnabled) return;
    let active = true;
    loadAvailableMethods().then((loaded) => {
      if (active) setMethods(loaded);
    });
    return () => {
      active = false;
    };
  }, [isIdvEnabled]);

  return idvSecurityOptionsFor(isIdvEnabled ? methods : [], currentKey);
};
